import { createContext, useCallback, useContext, useEffect, useState } from 'react';

import { useWebsocket } from '@zg-rentals/react-hooks';

import Constants from '../../constants/notification';
import { getNavDisplays } from '../../utils/displays';

import type React from 'react';
import type { User, WebSocketSubscribe } from '../../types';
import type { Props as HeaderNavigationProps } from '../HeaderNavigation';

const WsMessageTickContext = createContext(0);

export const useWsMessageTick = () => useContext(WsMessageTickContext);

export type Props = Partial<HeaderNavigationProps> & {
  // hides all navigation (and therefore every NotificationCenter) - see AppLayoutContainer
  hideNavigation?: boolean;
  // suppresses the mobile nav; the desktop nav still mounts but stays CSS-hidden below `xxl`
  isInMobileWebView?: boolean;
  children: React.ReactNode;
};

/*
 * Owns the single websocket connection notifications rely on: TopNavBar, VerticalNavigation, and
 * MobileHeaderNav each mount a NotificationCenter at once behind CSS breakpoints, so letting each
 * open its own socket would create duplicate connections against the same URL.
 *
 * `webSocketSource` is either a url to connect to directly (falling back to the hardcoded
 * per-brand default when omitted), or - when the consuming app already owns a connection to the
 * same backend, e.g. ZRM's RentalManagerWebSocketProvider - a subscribe function to listen on
 * that connection instead of opening a second one here.
 *
 * Connects/subscribes only when a NotificationCenter can actually become visible, mirroring
 * AppLayoutContainer's render paths:
 *   - !hideNavigation, since that drops every nav variant
 *   - loggedIn && displayMainNotificationCenter, the gate each render site already checks
 *   - !isInMobileWebView: that suppresses MobileHeaderNav, the only variant visible below the
 *     `xxl` breakpoint, and a mobile webview never reaches that breakpoint - so the desktop
 *     variants still mount but stay CSS-hidden and no notification UI is ever reachable.
 * Deliberately not gated on `isLoading`: it's transient, and toggling the url would tear down
 * and reconnect (with backoff) on every load transition.
 */
export const NotificationWebsocketProvider = ({
  children,
  hideNavigation = false,
  isInMobileWebView = false,
  webSocketSource,
  brand,
  userModel = {} as User,
  ...rest
}: Props) => {
  const { displays } = getNavDisplays({ ...rest, webSocketSource, brand, userModel });
  const shouldConnect =
    !hideNavigation && !isInMobileWebView && userModel.loggedIn && displays.displayMainNotificationCenter;
  const subscribe: WebSocketSubscribe | undefined = typeof webSocketSource === 'function' ? webSocketSource : undefined;
  const url = typeof webSocketSource === 'string' ? webSocketSource : undefined;
  const wsUrl =
    shouldConnect && !subscribe
      ? (url ?? Constants.WS_BRAND_CONNECTIONS[brand as keyof typeof Constants.WS_BRAND_CONNECTIONS])
      : undefined;

  const [wsMessageTick, setWsMessageTick] = useState(0);
  const bumpTick = useCallback(() => setWsMessageTick((tick) => tick + 1), []);

  useWebsocket(wsUrl, { onMessage: bumpTick });

  useEffect(() => {
    if (!shouldConnect || !subscribe) return undefined;

    return subscribe(bumpTick);
  }, [shouldConnect, subscribe, bumpTick]);

  return <WsMessageTickContext.Provider value={wsMessageTick}>{children}</WsMessageTickContext.Provider>;
};
